// client/src/components/comunicacao/agendadas/ComunicacaoAgendadaPage.js
import React, { useEffect, useCallback, useState } from 'react';
import { useComunicacoesAgendadas, useCancelarComunicacaoAgendada } from '../../../hooks/useComunicacaoAgendada';
import useAlert from '../../../hooks/useAlert';
import { Loader2, Clock, XCircle } from 'lucide-react';
import ComunicacaoAgendadaRow from './ComunicacaoAgendadaRow';
import PageHeader from '../../layout/PageHeader';
import ActionsDrawer from '../../common/ActionsDrawer';

// ✅ Fila de envios adiados (periodoEnvio.momento 'antes'/'depois' — ver
// server/models/subschemas/PeriodoEnvioSchema.js) — não tem criação manual
// aqui, os itens chegam automaticamente do disparo automático, do envio
// manual agendado e da descoberta proativa de lembretes. Esta tela só
// visualiza e permite cancelar itens ainda Pendente.
const STATUS_FILTERS = [
    { value: 'Pendente', label: 'Pendentes' },
    { value: 'Enviado', label: 'Enviados' },
    { value: 'Falha', label: 'Falhas' },
    { value: 'Cancelado', label: 'Cancelados' }
];

const ComunicacaoAgendadaPage = () => {
    const { setAlert } = useAlert();
    const [statusFiltro, setStatusFiltro] = useState('Pendente');
    const [selectedItem, setSelectedItem] = useState(null);

    const { data: itensData, isLoading, isError } = useComunicacoesAgendadas({ status: statusFiltro });
    const cancelarComunicacao = useCancelarComunicacaoAgendada();

    const itens = itensData || [];

    useEffect(() => {
        if (isError) setAlert('Erro ao carregar fila de comunicações agendadas', 'danger');
    }, [isError, setAlert]);

    const handleCancelar = useCallback((id) => {
        if (window.confirm('Deseja realmente cancelar este envio agendado?')) {
            cancelarComunicacao.mutate(id, { onSuccess: () => setSelectedItem(null) });
        }
    }, [cancelarComunicacao]);

    return (
        <>
            <div className="page-management-container">
                <PageHeader
                    icon={<Clock />}
                    title="Fila de Envio"
                    subtitle={!isLoading ? `${itens.length} item${itens.length !== 1 ? 'ns' : ''}` : null}
                    actions={(
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {STATUS_FILTERS.map((f) => (
                                <button
                                    key={f.value}
                                    type="button"
                                    className={`action-button ${statusFiltro === f.value ? 'action-button-primary' : 'action-button-secondary'}`}
                                    onClick={() => setStatusFiltro(f.value)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    )}
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando fila...</span>
                        </div>
                    ) : itens.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Template</th>
                                    <th>Cliente</th>
                                    <th>Evento</th>
                                    <th>Envio previsto</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itens.map((item) => (
                                    <ComunicacaoAgendadaRow
                                        key={item._id}
                                        item={item}
                                        onOpenActions={setSelectedItem}
                                    />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Clock size={48} />
                            <h3>Nenhum item {STATUS_FILTERS.find(f => f.value === statusFiltro)?.label.toLowerCase()} na fila</h3>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                title={selectedItem?.template?.nome || 'Envio agendado'}
                subtitle={selectedItem?.participanteInfo?.nome}
                actions={selectedItem?.status === 'Pendente' ? [
                    {
                        key: 'cancelar',
                        label: 'Cancelar envio',
                        icon: <XCircle size={20} className="icon" />,
                        onClick: () => handleCancelar(selectedItem._id),
                        destructive: true,
                        loading: cancelarComunicacao.isPending,
                    },
                ] : []}
            />
        </>
    );
};

export default ComunicacaoAgendadaPage;
